import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Tooltip,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import DashboardPanel from './DashboardPanel';

const Dashboard = ({
  dashboard,
  colors,
  onSave,
  onRefreshAll,
  onRefreshPanel,
  onRemovePanel,
  onTableRowAction,
  onTableBulkAction,
  onTableGlobalAction,
  onTableLinkClick,
  onTableConfigure,
  onTableFilterRemove,
  onTableAddFilter,
  onChartActionClick,
}) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleRefreshAll = async () => {
    setIsRefreshing(true);
    try {
      await onRefreshAll(dashboard.dashboardId);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(dashboard);
    } finally {
      setIsSaving(false);
    }
  };

  const panels = dashboard.panels || [];
  const layout = dashboard.layout || { type: 'grid', columns: 2 };
  const columns = isMobile ? 1 : layout.columns;

  // Empty state
  if (panels.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          p: 4,
          bgcolor: colors.background,
        }}
      >
        <Typography variant="h6" sx={{ color: colors.textSecondary, mb: 2 }}>
          No panels in this dashboard
        </Typography>
        <Typography variant="body2" sx={{ color: colors.textMuted, textAlign: 'center' }}>
          Pin tables or charts from your chat conversations to add them here.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: colors.background,
      }}
    >
      {/* Dashboard header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: `1px solid ${colors.border}`,
          bgcolor: colors.surface,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
            {dashboard.title}
          </Typography>
          <Typography variant="caption" sx={{ color: colors.textMuted }}>
            {panels.length} panel{panels.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh all panels">
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={isRefreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
                onClick={handleRefreshAll}
                disabled={isRefreshing}
                sx={{
                  borderColor: colors.border,
                  color: colors.textSecondary,
                  borderRadius: 0,
                  '&:hover': {
                    borderColor: '#22c55e',
                    color: '#22c55e',
                    bgcolor: 'rgba(34, 197, 94, 0.08)',
                  },
                }}
              >
                Refresh All
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Save dashboard">
            <span>
              <Button
                size="small"
                variant="contained"
                startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={isSaving}
                sx={{
                  bgcolor: '#22c55e',
                  color: colors.background,
                  borderRadius: 0,
                  '&:hover': {
                    bgcolor: '#16a34a',
                  },
                }}
              >
                Save
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Dashboard grid */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          p: 2,
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${columns}, 1fr)`,
            gap: 2,
            alignItems: 'start',
          }}
        >
          {panels.map((panel) => (
            <Box
              key={panel.panelId}
              sx={{
                minHeight: 300,
              }}
            >
              <DashboardPanel
                panel={panel}
                colors={colors}
                onRefresh={onRefreshPanel}
                onRemove={onRemovePanel}
                onTableRowAction={onTableRowAction}
                onTableBulkAction={onTableBulkAction}
                onTableGlobalAction={onTableGlobalAction}
                onTableLinkClick={onTableLinkClick}
                onTableConfigure={onTableConfigure}
                onTableFilterRemove={onTableFilterRemove}
                onTableAddFilter={onTableAddFilter}
                onChartActionClick={onChartActionClick}
              />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
